import { useMemo, useRef, useState } from 'react';
import {
    FaBalanceScale,
    FaChartLine,
    FaClipboardCheck,
    FaComments,
    FaFileAlt,
    FaGlobeAmericas,
    FaSearch,
    FaShieldAlt,
    FaUserCheck,
    FaUsers,
} from 'react-icons/fa';
import { readValue } from '../../../../../components/InputField/helper';
import { VARIANT } from '../../../../../util/variant';
import BusinessV2FieldGrid from '../BusinessV2FieldGrid';
import './BusinessV2SectionedFieldStep.css';

const DEFAULT_SECTION_ICON_MAP = {
    default: FaClipboardCheck,
    regulatory: FaBalanceScale,
    program: FaShieldAlt,
    audit: FaSearch,
    policies: FaFileAlt,
    cdd: FaUsers,
    monitoring: FaChartLine,
    training: FaUserCheck,
    sanctions: FaGlobeAmericas,
    terms: FaComments,
};

const normalizeSectionVariant = (value) => (value === 'separated' ? 'separated' : 'card');
const toTemplateKey = (field) => (typeof field === 'string' ? field.replace(/\[\d+\]/g, '[]') : field);

const normalizeSectionBreakVariant = (value) => (value === 'intro' ? 'intro' : 'callout');

const hasAnsweredValue = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === 'boolean' || typeof value === 'number') {
        return true;
    }

    if (typeof value === 'string') {
        return value.trim() !== '';
    }

    return value !== undefined && value !== null;
};

const resolveSections = ({
    sectionConfigs = [],
    sectionBreaks = [],
    visibleFieldOrder = [],
    data,
    slotIds = [],
}) => {
    const groupedFieldSet = new Set();

    const sections = (Array.isArray(sectionConfigs) ? sectionConfigs : [])
        .map((sectionConfig) => {
            const configuredFieldOrder = Array.isArray(sectionConfig?.fields) ? sectionConfig.fields : [];
            const configuredTemplates = new Set(configuredFieldOrder.map(toTemplateKey));
            const fieldOrder = visibleFieldOrder.filter(
                (field) => configuredTemplates.has(toTemplateKey(field)),
            );

            if (!fieldOrder.length && !slotIds.includes(sectionConfig?.id)) {
                return null;
            }

            fieldOrder.forEach((field) => groupedFieldSet.add(field));

            const sectionBreak = sectionBreaks.find((candidateBreak) => (
                typeof candidateBreak?.beforeField === 'string'
                && fieldOrder.includes(candidateBreak.beforeField)
            )) || null;
            const sectionBreakIndex = sectionBreak?.beforeField
                ? fieldOrder.indexOf(sectionBreak.beforeField)
                : -1;

            return {
                ...sectionConfig,
                fieldOrder,
                beforeBreakFieldOrder: sectionBreakIndex === -1 ? fieldOrder : fieldOrder.slice(0, sectionBreakIndex),
                afterBreakFieldOrder: sectionBreakIndex === -1 ? [] : fieldOrder.slice(sectionBreakIndex),
                sectionBreak,
                isComplete: fieldOrder.every((field) => hasAnsweredValue(readValue(data, field))),
            };
        })
        .filter(Boolean);

    const ungroupedFieldOrder = visibleFieldOrder.filter((field) => !groupedFieldSet.has(field));
    if (ungroupedFieldOrder.length) {
        sections.push({
            id: 'additional-details',
            title: 'Additional Details',
            description: '',
            icon: 'default',
            fieldOrder: ungroupedFieldOrder,
            beforeBreakFieldOrder: ungroupedFieldOrder,
            afterBreakFieldOrder: [],
            sectionBreak: null,
            isComplete: ungroupedFieldOrder.every((field) => hasAnsweredValue(readValue(data, field))),
        });
    }

    if (!sections.length && visibleFieldOrder.length) {
        sections.push({
            id: 'details',
            title: 'Details',
            description: '',
            icon: 'default',
            fieldOrder: visibleFieldOrder,
            beforeBreakFieldOrder: visibleFieldOrder,
            afterBreakFieldOrder: [],
            sectionBreak: null,
            isComplete: visibleFieldOrder.every((field) => hasAnsweredValue(readValue(data, field))),
        });
    }

    return sections;
};

const SectionNavItem = ({
    section,
    index,
    isActive,
    onClick,
    iconMap,
}) => {
    const Icon = iconMap?.[section.icon] || iconMap?.default || FaClipboardCheck;

    return (
        <button
            type='button'
            className={`business-v2-sectioned-step__nav-item ${isActive ? 'business-v2-sectioned-step__nav-item--active' : ''}`}
            onClick={onClick}
        >
            <span className='business-v2-sectioned-step__nav-index'>
                {String(index + 1).padStart(2, '0')}
            </span>
            <span className='business-v2-sectioned-step__nav-icon'>
                <Icon size={14} />
            </span>
            <span className='business-v2-sectioned-step__nav-copy'>
                <span className='business-v2-sectioned-step__nav-title'>{section.title}</span>
                {section.description ? (
                    <span className='business-v2-sectioned-step__nav-description'>{section.description}</span>
                ) : null}
            </span>
        </button>
    );
};

const SectionCard = ({
    section,
    index,
    sectionVariant,
    showSectionBadge,
    fieldMap,
    data,
    asLabel,
    handleChange,
    hasError,
    onFieldError,
    sectionRefs,
    setActiveSectionId,
    iconMap,
    dataTestIdPrefix,
    sectionSlot,
    variant,
}) => {
    const Icon = iconMap?.[section.icon] || iconMap?.default || FaClipboardCheck;
    const sectionBreakVariant = normalizeSectionBreakVariant(section.sectionBreak?.variant);
    const isRuleSeparated = variant === VARIANT.V2;

    const slotContent = sectionSlot
        ? (typeof sectionSlot === 'function' ? sectionSlot(section) : sectionSlot)
        : null;
    const hasFields = Boolean(section.beforeBreakFieldOrder?.length || section.afterBreakFieldOrder?.length);
    const hasSectionBreak = Boolean(section.sectionBreak?.title || (section.sectionBreak?.paragraphs || []).length);
    if (sectionSlot && slotContent == null && !hasFields && !hasSectionBreak) {
        return null;
    }

    return (
        <section
            ref={(element) => {
                sectionRefs.current[section.id] = element;
            }}
            className={`business-v2-sectioned-step__section-card business-v2-sectioned-step__section-card--${sectionVariant}${isRuleSeparated ? ' business-v2-sectioned-step__section-card--rule' : ''}`}
            onMouseEnter={() => setActiveSectionId(section.id)}
            data-testid={`${dataTestIdPrefix}-section-${section.id}`}
            {...(isRuleSeparated ? { 'data-section-treatment': 'rule' } : {})}
        >
            {/* A rule-separated section can legitimately be untitled: Figma 19:236 draws
                the identity card as three groups divided by hairlines with no headings
                of their own. Rendering the header regardless left an empty <h3> and its
                14px margin at the top of each group, which read as inconsistent spacing.
                Every legacy caller titles its sections, so nothing changes for them. */}
            {(section.title || section.description) ? (
            <div className={`business-v2-sectioned-step__section-header ${showSectionBadge ? 'business-v2-sectioned-step__section-header--with-badge' : ''}`.trim()}>
                {showSectionBadge ? (
                    <div className='business-v2-sectioned-step__section-badge'>
                        {String(index + 1).padStart(2, '0')}
                    </div>
                ) : null}
                {isRuleSeparated ? null : (
                    <div className='business-v2-sectioned-step__section-icon'>
                        <Icon size={18} />
                    </div>
                )}
                <div className='business-v2-sectioned-step__section-title-wrap'>
                    <h3 className='business-v2-sectioned-step__section-title'>{section.title}</h3>
                    {section.description ? (
                        <p className='business-v2-sectioned-step__section-description'>
                            {section.description}
                        </p>
                    ) : null}
                </div>
            </div>
            ) : null}

            {/* A section whose fields are repeatable (individuals[], entities[]) cannot
                be a flat grid — it needs its own card component. A slot lets the caller
                render that section itself while keeping the heading, nav and layout. */}
            {slotContent}

            {!sectionSlot && !!section.beforeBreakFieldOrder.length && (
                <BusinessV2FieldGrid
                    fieldOrder={section.beforeBreakFieldOrder}
                    fieldMap={fieldMap}
                    data={data}
                    asLabel={asLabel}
                    handleChange={handleChange}
                    hasError={hasError}
                    onFieldError={onFieldError}
                    rowClassName='row gx-4 gy-3 business-v2-field-grid business-v2-sectioned-step__field-grid'
                    dataTestId={`${dataTestIdPrefix}-fields-${section.id}-primary`}
                    variant={variant}
                />
            )}

            {(section.sectionBreak?.title || (section.sectionBreak?.paragraphs || []).length) ? (
                <div className={`business-v2-sectioned-step__callout business-v2-sectioned-step__callout--${sectionBreakVariant}`}>
                    {section.sectionBreak?.eyebrow ? (
                        <p className='business-v2-sectioned-step__callout-eyebrow'>{section.sectionBreak.eyebrow}</p>
                    ) : null}

                    {section.sectionBreak?.title ? (
                        <h4 className='business-v2-sectioned-step__callout-title'>{section.sectionBreak.title}</h4>
                    ) : null}

                    {(section.sectionBreak?.paragraphs || []).map((paragraph, paragraphIndex) => (
                        <p key={`${section.id}-callout-${paragraphIndex}`} className='business-v2-step-copy business-v2-sectioned-step__callout-copy'>
                            {paragraph}
                        </p>
                    ))}
                </div>
            ) : null}

            {!sectionSlot && !!section.afterBreakFieldOrder.length && (
                <BusinessV2FieldGrid
                    fieldOrder={section.afterBreakFieldOrder}
                    fieldMap={fieldMap}
                    data={data}
                    asLabel={asLabel}
                    handleChange={handleChange}
                    hasError={hasError}
                    onFieldError={onFieldError}
                    rowClassName='row gx-4 gy-3 business-v2-field-grid business-v2-sectioned-step__field-grid'
                    dataTestId={`${dataTestIdPrefix}-fields-${section.id}-secondary`}
                    variant={variant}
                />
            )}
        </section>
    );
};

const EMPTY_SLOTS = {};

const BusinessV2SectionedFieldStep = ({
    title,
    titleDescription = '',
    titleIcon = 'default',
    wizardContent = {},
    visibleFieldOrder = [],
    fieldMap = {},
    data,
    asLabel,
    handleChange,
    hasError,
    onFieldError,
    showSectionNavigator = true,
    hideHeader = false,
    iconMap = DEFAULT_SECTION_ICON_MAP,
    dataTestIdPrefix = 'business-v2-sectioned-step',
    className = '',
    sectionSlots = EMPTY_SLOTS,
    leadContent = null,
    variant,
}) => {
    const sectionRefs = useRef({});
    const [activeSectionId, setActiveSectionId] = useState(null);
    const heroIconKey = wizardContent?.hero?.icon || titleIcon;
    const heroDescription = wizardContent?.hero?.description || titleDescription;
    const slotIds = useMemo(() => Object.keys(sectionSlots || {}), [sectionSlots]);
    const sections = useMemo(() => resolveSections({
        sectionConfigs: wizardContent?.uiSections,
        sectionBreaks: wizardContent?.sectionBreaks,
        visibleFieldOrder,
        data,
        slotIds,
    }), [data, visibleFieldOrder, wizardContent?.sectionBreaks, wizardContent?.uiSections, slotIds]);
    const resolvedActiveSectionId = activeSectionId || sections[0]?.id || null;
    const HeroIcon = iconMap?.[heroIconKey] || iconMap?.default || FaClipboardCheck;
    const sectionVariant = normalizeSectionVariant(wizardContent?.sectionVariant);
    const showSectionBadge = Boolean(wizardContent?.showSectionBadge);
    const resolvedShowSectionNavigator = typeof wizardContent?.showSectionNavigator === 'boolean'
        ? wizardContent.showSectionNavigator
        : showSectionNavigator;
    const shouldShowNavigator = resolvedShowSectionNavigator && sections.length > 1;

    const scrollToSection = (sectionId) => {
        setActiveSectionId(sectionId);
        sectionRefs.current[sectionId]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    return (
        <div className={`business-v2-sectioned-step business-v2-sectioned-step--${sectionVariant} ${className}`.trim()} data-testid={dataTestIdPrefix}>
            {hideHeader ? null : (
                <div className='business-v2-step-header business-v2-sectioned-step__header'>
                    <div className='business-v2-sectioned-step__hero'>
                        <div className='business-v2-sectioned-step__hero-icon'>
                            <HeroIcon size={20} />
                        </div>
                        <div className='business-v2-sectioned-step__hero-copy'>
                            <h2 className='business-v2-step-title'>{title}</h2>
                            {heroDescription ? (
                                <p className='business-v2-step-copy business-v2-sectioned-step__hero-description'>
                                    {heroDescription}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            <div className={`business-v2-sectioned-step__layout ${!shouldShowNavigator ? 'business-v2-sectioned-step__layout--single-column' : ''}`.trim()}>
                {shouldShowNavigator ? (
                    <aside className='business-v2-sectioned-step__nav' aria-label={`${title} sections`}>
                        <p className='business-v2-sectioned-step__nav-eyebrow'>Questionnaire Flow</p>
                        <div className='business-v2-sectioned-step__nav-list'>
                            {sections.map((section, index) => (
                                <SectionNavItem
                                    key={section.id}
                                    section={section}
                                    index={index}
                                    isActive={resolvedActiveSectionId === section.id}
                                    onClick={() => scrollToSection(section.id)}
                                    iconMap={iconMap}
                                />
                            ))}
                        </div>
                    </aside>
                ) : null}

                <div className='business-v2-step-form-stack business-v2-sectioned-step__sections'>
                    {leadContent}
                    {sections.map((section, index) => (
                        <SectionCard
                            key={section.id}
                            section={section}
                            index={index}
                            fieldMap={fieldMap}
                            data={data}
                            showSectionBadge={showSectionBadge}
                            asLabel={asLabel}
                            handleChange={handleChange}
                            hasError={hasError}
                            onFieldError={onFieldError}
                            sectionVariant={sectionVariant}
                            sectionRefs={sectionRefs}
                            setActiveSectionId={setActiveSectionId}
                            iconMap={iconMap}
                            dataTestIdPrefix={dataTestIdPrefix}
                            sectionSlot={sectionSlots?.[section.id]}
                            variant={variant}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BusinessV2SectionedFieldStep;
